import { Button, Icon, Input, InputWrapper, Modal } from "@components";
import { formatDate, setProductVariation } from "@utilities/functions";
import { AddSupplyInputType, Color, ColorSizeEntry, Product, Size, Variations } from "@utilities/types";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { ProductVariation } from "../../components";
import { ReadProductVariation } from "./ReadProductVariation";

interface Props {
    isOpen: boolean;
    product: Product | null;
    onClose: () => void;
    onSupply: (newSupply: AddSupplyInputType) => void;
}

export const SupplyModal:FC<Props> = ({
    isOpen,
    product,
    onClose,
    onSupply,
})=> {

    const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(0);
    const [newProductVariation, setNewProductVariation] = useState<Variations | null>(null);
    const [initialVariation, setInitialVariation] = useState<Variations | null>(null);
    const [note, setNote] = useState<string>("");

    const isDisabled = useMemo(() => {
        if(initialVariation && newProductVariation){
            const isSame = JSON.stringify(initialVariation) === JSON.stringify(newProductVariation);
            if(isSame) return true

            if ('quantity' in newProductVariation) {
                const isQuantityValid = newProductVariation.quantity > 0;
                return !isQuantityValid;
            }
            if ('sizes' in newProductVariation) {
                if(!newProductVariation.sizes) return true;
                if(newProductVariation.sizes.length === 0) return true;

                const isSizeValid = newProductVariation.sizes.every((size: Size) => size.quantity! > 0);
                return !isSizeValid;
            }
            if ('colors' in newProductVariation) {
                if(!newProductVariation.colors) return true;
                if(newProductVariation.colors.length === 0) return true;

                const isColorValid = newProductVariation.colors.every((color: Color) => color.quantity! > 0);
                return !isColorValid;
            }
            if ('colorSizeData' in newProductVariation) {
                if(!newProductVariation.colorSizeData) return true;
                if(newProductVariation.colorSizeData.length === 0) return true;

                const isColorSizeValid = newProductVariation.colorSizeData.every((colorSize: ColorSizeEntry) => {
                    const isSizeValid = colorSize.sizes.every((size: Size) => size.quantity! > 0);
                    return isSizeValid;
                });
                return !isColorSizeValid;
            }
        }
        return true;
    }, [initialVariation, newProductVariation]);

    useEffect(()=> {
        if(product){
            const initialVariationTmp = setProductVariation(product);
            setNewProductVariation(initialVariationTmp);
            setInitialVariation(initialVariationTmp);
        }
    }, [product]);

    const handleClose = useCallback(() => {
        setSelectedOptionIndex(0);
        setNote("");
        setNewProductVariation(null);
        onClose();
    }, [onClose]);

    const handleSupply = useCallback(() => {
        if(!newProductVariation) return;

        onSupply({
            supplyVariation: newProductVariation,
            note,
        });
        handleClose();
    }, [newProductVariation, note, onSupply, handleClose]);

    const renderCurrentStock = useCallback(()=> (
        <div className="mt-6">
            <h2 className="text-xl font-bold border-b pb-2 mb-4 text-primary">
                Stock actuel
            </h2>

            <div className="space-y-4">
                <ReadProductVariation
                    variation={product?.variations}
                />
            </div>
        </div>
    ), [product]);

    // console.log({newProductVariation})

    const renderNewSupply = useCallback(()=> (
        <div className="mt-6">
            <h2 className="text-xl font-bold border-b pb-2 mb-4 text-primary">
                Nouvel approvisionnement
            </h2>

            <div className="space-y-4">
                <ProductVariation
                    initialVariation={newProductVariation}
                    forceVariationType={product?.variationType}
                    onChange={(variationType: string, data: any)=> {
                        setNewProductVariation(data);
                    }}
                />

                <InputWrapper label="Note d'approvionnement (facultatif)">
                    <Input 
                        type="textarea"
                        placeholder="Ajouter une note..."
                        value={note}
                        onChange={(e)=> setNote(e.target.value)}
                    />
                </InputWrapper>

                <div className="flex justify-end">
                    <Button
                        className={`${!newProductVariation ? "btn-disabled" : "btn-success"}`}
                        onClick={handleSupply}
                        disabled={isDisabled}
                    >
                        Approvisionner
                    </Button>
                </div>
            </div>
        </div>
    ), [product, isDisabled, note, newProductVariation, handleSupply]);

    const renderSupplyHistory = useCallback(()=> (
        <div className="mt-6">
            <h2 className="text-xl font-bold border-b pb-2 mb-4 text-primary">
                Historique des approvisionnements
            </h2>

            <div className="space-y-4">
                {product?.supplyHistory.map((supply, index) => (
                    <div key={index} className="bg-white p-4 rounded-lg shadow border border-gray-200">
                        <p className="text-sm text-gray-600">
                        <strong className="text-black">Date :</strong> {formatDate(supply.date)}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                        <strong className="text-black">Note :</strong> {supply.note || 'Aucune note'}
                        </p>

                        <div className="mt-2">
                            <p className="font-semibold mb-1 text-gray-700">Détails :</p>
                            <ReadProductVariation 
                                variation={supply.variation}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    ), [product]);

    const renderSelectedOption = useCallback(()=> {
        switch(selectedOptionIndex) {
            case 0:
                return renderCurrentStock();
            case 1:
                return renderNewSupply();
            case 2:
                return renderSupplyHistory();
            default:
                return null;
        }
    }, [
        selectedOptionIndex, renderNewSupply,
        renderCurrentStock, renderSupplyHistory,
    ]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={`Approvisionnement de ${product?.name}`}
        >
            <div className="flex flex-col md:flex-row md:flex-wrap gap-4">
                <Button 
                    className={`${selectedOptionIndex === 0 ? "btn-success" : "btn-neutral"}`}
                    onClick={()=> setSelectedOptionIndex(0)}
                >
                    <Icon iconName='Cuboid' size={20} /> Stock actuel
                </Button>

                <Button 
                    className={`${selectedOptionIndex === 1 ? "btn-success" : "btn-neutral"}`}
                    onClick={()=> setSelectedOptionIndex(1)}
                >
                    <Icon iconName='Plus' size={20} /> Nouvel approvisionnement
                </Button>

                <Button 
                    className={`${selectedOptionIndex === 2 ? "btn-success" : "btn-neutral"}`}
                    onClick={()=> setSelectedOptionIndex(2)}
                >
                    <Icon iconName='History' size={20} /> Historique des approvisionnements
                </Button>
            </div>

            {renderSelectedOption()}
        </Modal>
    )
}
